import { z } from "zod";

export const pickerOptionSchema = z.object({
  label: z.string(),
  value: z.string(),
  roleAssignmentTarget: z.string(),
});

export const roleSchema = z.object({
  role: z.string().min(1, "Role is required"),
  scope: z.string().min(1, "Scope is required"),
  resourceViewerOptions: z
    .object({
      accountGroups: z.array(pickerOptionSchema),
    })
    .optional(),
  policyDeployerOptions: z
    .object({
      accountGroups: z.array(pickerOptionSchema),
    })
    .optional(),
  creatorOptions: z
    .object({
      accountGroups: z.boolean().optional(),
      policyCollections: z.boolean().optional(),
      policyRepositories: z.boolean().optional(),
    })
    .optional(),
  editorOptions: z
    .object({
      accountGroups: z.array(pickerOptionSchema).optional(),
      policyCollections: z.array(pickerOptionSchema).optional(),
      policyRepositories: z.array(pickerOptionSchema).optional(),
    })
    .optional(),
  ownerOptions: z
    .object({
      accountGroups: z.array(pickerOptionSchema).optional(),
      policyCollections: z.array(pickerOptionSchema).optional(),
      policyRepositories: z.array(pickerOptionSchema).optional(),
    })
    .optional(),
});

export type RoleFormValues = z.infer<typeof roleSchema>;

export const groupSchema = z.object({
  name: z.string().min(1, "Group Name is required"),
  displayName: z.string().optional(),
  roles: z
    .array(roleSchema)
    .min(1, "At least one role is required")
    .refine(
      (roles) => {
        const roleNames = roles.map((r) => r.role).filter(Boolean);
        return new Set(roleNames).size === roleNames.length;
      },
      {
        message: "Each role can only be selected once",
      },
    ),
});

export type GroupFormSchema = z.infer<typeof groupSchema>;

export const getDefaultRole = () => ({
  role: "",
  scope: "GRANT_SYSTEM_ALL",
  resourceViewerOptions: {
    accountGroups: [],
  },
  policyDeployerOptions: {
    accountGroups: [],
  },
  creatorOptions: {
    accountGroups: false,
    policyCollections: false,
    policyRepositories: false,
  },
  editorOptions: {
    accountGroups: [],
    policyCollections: [],
    policyRepositories: [],
  },
  ownerOptions: {
    accountGroups: [],
    policyCollections: [],
    policyRepositories: [],
  },
});

export const getDefaultGroupValues = (): GroupFormSchema => ({
  name: "",
  displayName: "",
  roles: [getDefaultRole()],
});
